// §20 Finance — Refund confirm dialog (BANK_TRANSFER). Extracted so both the
// detail action-bar and the §19 refund-queue per-row [Confirm] reuse one form.
// ReturnApi.confirmRefund sends Idempotency-Key + If-Match; this component only
// collects the manual transaction details (incl. the optional transfer
// screenshot) and toasts itself (HttpRequest has no global toaster).
import { Alert, Button, CopyButton, Group, List, Modal, Paper, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { WarningCircleIcon } from "@phosphor-icons/react";
import { nextStepToast, showNotification } from "@dehub/theme";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReturnApi } from "../../apis";
import { IReturnBankAccount } from "../../apis/return";
import { currencyFormat } from "../../utils";
import { PhotoUploader } from "../../pages/return/warehouse-receipt/PhotoUploader";

// Minimal shape both IReturnDetail (id) and IReturnSummary (returnRequestId) can
// supply — the caller normalises the id so this component stays source-agnostic.
export interface IRefundConfirmTarget {
  id: string;
  returnNumber?: string | null;
  version: number;
  refundAmount?: number | null;
  refundMethod?: string | null;
  // §20 context — buyer line (BU-xxxxx); shown when provided.
  buyerName?: string | null;
  buyerRefCode?: string | null;
  buyerBankAccount?: IReturnBankAccount | null;
}

interface Props {
  target: IRefundConfirmTarget | null;
  opened: boolean;
  onClose: () => void;
  onConfirmed: () => void;
}

export function RefundConfirmModal ({ target, opened, onClose, onConfirmed }: Props){
  const { t } = useTranslation("returns");
  const [bankRef, setBankRef] = useState("");
  const [transferredAt, setTransferredAt] = useState<string | null>(null);
  const [bankReference, setBankReference] = useState("");
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const [bankAcct, setBankAcct] = useState<IReturnBankAccount | null>(null);

  // Reset the form each time the dialog (re)opens so a prior row's values never
  // leak into the next confirmation.
  useEffect(() => {
    if (!opened) return;
    setBankRef("");
    setTransferredAt(null);
    setBankReference("");
    setScreenshotUrl(null);
    setNotes("");
    setTouched(false);
    if (target?.buyerBankAccount !== undefined) {
      setBankAcct(target.buyerBankAccount);
    } else if (target?.id) {
      setBankAcct(null);
      ReturnApi.get(target.id)
        .then((r) => setBankAcct(r.returnRequest.buyerBankAccount ?? null))
        .catch(() => setBankAcct(null));
    } else {
      setBankAcct(null);
    }
  }, [opened, target?.id, target?.buyerBankAccount]);

  const refOk = bankRef.trim().length >= 3 && bankRef.trim().length <= 128;
  const valid = refOk && !!transferredAt;

  const handleConfirm = async () => {
    if (!target) return;
    if (!valid) return;
    setBusy(true);
    try {
      await ReturnApi.confirmRefund(target.id, {
        bankTransactionRef  : bankRef.trim(),
        transferredAt       : new Date(transferredAt as string).toISOString(),
        bankReference        : bankReference.trim() || null,
        transferScreenshotUrl: screenshotUrl || null,
        notes               : notes.trim() || null,
        expectedVersion     : target.version
      });
      // @dehub/help toast: what happens next (R8); the locale text stays the fallback.
      nextStepToast("order.returns-detail.toast.refund", { fallback: { title: t("toast.success"), message: t("refundConfirm.done") } });
      onClose();
      onConfirmed();
    } catch (e) {
      showNotification({ color: "red", title: t("toast.error"), message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={2}>
          <Text fz="var(--nw-fz-drawer)" fw={650}>{t("refundConfirm.title")}</Text>
          <Text size="sm" c="var(--nw-text-2)">
            {target?.returnNumber ?? "-"} · {t("refundConfirm.amountLine", { amount: target?.refundAmount != null ? currencyFormat(target.refundAmount) : "-" })}
          </Text>
        </Stack>
      }
      size={440}
    >
      <Stack gap="md">
        {(target?.buyerName || target?.buyerRefCode) ? (
          <Group justify="space-between" gap="xs" wrap="nowrap">
            <Text size="sm" c="dimmed">{t("filters.buyer")}</Text>
            <Text size="sm" fw={600}>
              {target.buyerName ?? "-"}
              {target.buyerRefCode ? <Text span size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}> · {target.buyerRefCode}</Text> : null}
            </Text>
          </Group>
        ) : null}
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Text size="sm" c="dimmed">{t("resolutionPanel.refundAmount")}</Text>
          <Text size="sm" fw={600}>
            {target?.refundAmount != null ? currencyFormat(target.refundAmount) : "-"}
            {target?.refundMethod ? ` · ${t(`refundMethod.${target.refundMethod}`, { defaultValue: "-" })}` : ""}
          </Text>
        </Group>

        {bankAcct?.number ? (
          <Paper withBorder radius="md" p="sm" bg="var(--mantine-color-gray-0)">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={2}>
                <Text size="sm" c="dimmed">{t("refundConfirm.receiverAccount")}</Text>
                {bankAcct.bankName ? <Text size="sm" c="dimmed">{bankAcct.bankName}</Text> : null}
                <Text size="lg" fw={700} style={{ fontVariantNumeric: "tabular-nums", letterSpacing: 0.5 }}>
                  {bankAcct.number}
                </Text>
                {bankAcct.name ? <Text size="sm" fw={500}>{bankAcct.name}</Text> : null}
              </Stack>
              <CopyButton value={bankAcct.number ?? ""}>
                {({ copied, copy }) => (
                  <Button size="sm" variant={copied ? "filled" : "light"} onClick={copy}>
                    {copied ? t("refundConfirm.copied") : t("refundConfirm.copy")}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </Paper>
        ) : (
          <Text size="sm" c="dimmed">{t("refundConfirm.noAccount")}</Text>
        )}

        <Text size="sm" fw={600}>{t("refundConfirm.transactionInfo")}</Text>
        <TextInput
          label={t("refundConfirm.transactionId")}
          required
          value={bankRef}
          onChange={(e) => setBankRef(e.currentTarget.value)}
          onBlur={() => setTouched(true)}
          error={(touched || bankRef.length > 0) && !refOk ? t("refundConfirm.refLength") : undefined}
        />
        <DateTimePicker
          label={t("refundConfirm.transferredAt")}
          required
          value={transferredAt}
          onChange={setTransferredAt}
          placeholder={t("refundConfirm.dateTimePlaceholder")}
          valueFormat="YYYY-MM-DD HH:mm"
          error={touched && !transferredAt ? t("refundConfirm.dateRequired") : undefined}
          clearable
        />
        <TextInput
          label={t("refundConfirm.bankReference")}
          value={bankReference}
          onChange={(e) => setBankReference(e.currentTarget.value)}
          placeholder={t("refundConfirm.bankReferencePlaceholder")}
        />

        <Stack gap={4}>
          <Text size="sm" fw={500}>{t("refundConfirm.screenshot")}</Text>
          <PhotoUploader
            value={screenshotUrl ? [screenshotUrl] : []}
            onChange={(urls) => setScreenshotUrl(urls.length ? urls[urls.length - 1] : null)}
            max={1}
          />
        </Stack>

        <Textarea
          label={t("shippingPanel.descriptionOptional")}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />

        <Alert color="gray" variant="light" icon={<WarningCircleIcon size={16} />} title={t("refundConfirm.afterConfirm")}>
          <List size="sm" spacing={2}>
            <List.Item>{t("refundConfirm.effect.completed")}</List.Item>
            <List.Item>{t("refundConfirm.effect.buyerNotified")}</List.Item>
            <List.Item>{t("refundConfirm.effect.audit")}</List.Item>
          </List>
        </Alert>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t("actionsDialog.cancel")}</Button>
          <Button onClick={() => { setTouched(true); handleConfirm(); }} loading={busy} disabled={busy}>{t("refundConfirm.submit")}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
